'use client';

import { useRouter } from 'next/navigation';

import { Connectivity, OptionType, PlanType } from '@app-types/product';

import { optionRoute, phoneRoute } from '@helpers/routes';

import { cartStore } from '@stores/cart';
import { uiStore } from '@stores/ui';

const ELIGIBITY_MODE: 'focus' | 'gamme' = 'gamme';
export function useHome() {
    const router = useRouter();

    // Sélecteurs optimisés pour éviter les re-renders globaux
    const selectedPlan = cartStore((state) => state.selectedPlan);
    const setSelectedPlan = cartStore((state) => state.setSelectedPlan);
    const selectedOption = cartStore((state) => state.selectedOption);
    const setSelectedOption = cartStore((state) => state.setSelectedOption);
    const addToCart = cartStore((state) => state.addToCart);
    const byouCart = cartStore((state) => state.byouCart);
    const selectedFaiOption = cartStore((state) => state.selectedFaiOption);
    const selectedFaiProduct = cartStore((state) => state.selectedFaiProduct);
    const setSelectedFaiProduct = cartStore((state) => state.setSelectedFaiProduct);
    const setSelectedFaiOption = cartStore((state) => state.setSelectedFaiOption);
    const selectedNetwork = cartStore((state) => state.selectedNetwork);
    const setSelectedNetwork = cartStore((state) => state.setSelectedNetwork);
    const temporarilySelectedPlan = cartStore((state) => state.temporarilySelectedPlan);
    const setTemporarilySelectedPlan = cartStore((state) => state.setTemporarilySelectedPlan);
    const setTemporarilySelectedFaiProduct = cartStore((state) => state.setTemporarilySelectedFaiProduct);
    const temporarilySelectedFaiProduct = cartStore((state) => state.temporarilySelectedFaiProduct);
    const otisConfigurator = cartStore((state) => state.otisConfigurator);
    const setOtisConfigurator = cartStore((state) => state.setOtisConfigurator);
    const switchJourney = cartStore((state) => state.switchJourney);
    const setHasOptionsAvailable = uiStore((state) => state.setHasOptionsAvailable);

    const getNextRoute = (isPhoneStepEnabled = false): string => {
        // If phone step is enabled, it takes priority
        if (isPhoneStepEnabled) {
            return phoneRoute.path;
        }

        return optionRoute.path;
    };

    const handleAddToCart = ({ isPhoneStepEnabled = false }) => {
        if (!selectedPlan) {
            if (selectedFaiProduct) {
                // const trackerId = await trackerIdService();
                const urlParams = {
                    faiOptionId: selectedFaiOption ?? undefined,
                    eligModeParams: `${selectedFaiProduct.range_ng}_${selectedFaiProduct.commercial_series}_${selectedFaiProduct.technology}`,
                };

                return router.push(OFFRES_INTERNET.eligPanierMixte(urlParams));
            }
            return;
        }

        const cartOptions: OptionType[] = [];
        if (selectedOption?.gencode) {
            const t = selectedPlan.options.find((o) => o.gencode === selectedOption?.gencode);
            if (t) cartOptions.push(t);
        }

        setHasOptionsAvailable(null);

        addToCart({
            product: { ...selectedPlan },
            options: cartOptions,
        });
        if (selectedFaiProduct) {
            let neededProducts: string[] = [];
            if (selectedPlan.neededElements?.length) {
                const autoAdd = selectedPlan.neededElements.flatMap((el) =>
                    el.alternatives.map((alt) => alt.identifier),
                );
                neededProducts = [...autoAdd];
            }

            const urlParams = {
                planId: selectedPlan?.gencode,
                neededProducts: neededProducts.length > 0 ? neededProducts.join(',') : undefined,
                optionsId:
                    selectedPlan.connectivity === Connectivity.FOUR_G && selectedOption?.gencode
                        ? selectedOption?.gencode
                        : undefined,
                faiOptionId: selectedFaiOption ?? undefined,
                eligModeParams: `${selectedFaiProduct.range_ng}_${selectedFaiProduct.commercial_series}_${selectedFaiProduct.technology}`,
            };

            return router.push(OFFRES_INTERNET.eligPanierMixte(urlParams));
        }
        router.push(getNextRoute(isPhoneStepEnabled));
    };

    const handleAddCatalogProductToCart = ({
        product,
        options,
        isPhoneStepEnabled = false,
    }: {
        product: PlanType;
        options: OptionType[];
        isPhoneStepEnabled?: boolean;
    }): void => {
        setHasOptionsAvailable(null);
        addToCart({
            product,
            options,
        });
        router.push(getNextRoute(isPhoneStepEnabled));
    };

    return {
        selectedPlan,
        selectedOption,
        setSelectedOption,
        setSelectedPlan,
        selectedFaiProduct,
        setSelectedFaiProduct,
        addToCart,
        handleAddToCart,
        byouCart,
        handleAddCatalogProductToCart,
        otisConfigurator,
        setOtisConfigurator,
        switchJourney,
        setSelectedFaiOption,
        selectedFaiOption,
        selectedNetwork,
        setSelectedNetwork,
        temporarilySelectedPlan,
        setTemporarilySelectedPlan,
        setTemporarilySelectedFaiProduct,
        temporarilySelectedFaiProduct,
    };
}

export const OFFRES_INTERNET = {
    eligPanierMixte: ({
        planId,
        neededProducts,
        optionsId,
        faiOptionId,
        trackerId,
        eligModeParams,
    }: {
        planId?: string;
        neededProducts?: string;
        optionsId?: string;
        faiOptionId?: string;
        trackerId?: string;
        eligModeParams: string;
    }): string => {
        const baseUrl = '/offres-internet/test-eligibilite-panier-mixte';
        const params = new URLSearchParams();
        if (planId) params.append('plan_id', planId);
        if (optionsId) params.append('options_id', optionsId);
        if (faiOptionId) params.append('fai_options', faiOptionId);
        if (trackerId) params.append('tracker_id', trackerId);
        if (neededProducts) params.append('needed_products', neededProducts);

        params.append(ELIGIBITY_MODE, eligModeParams);

        const queryString = params.toString();
        return `${baseUrl}?${queryString}`;
    },
};
